/* eslint-disable import/extensions */
import {
  Query,
  Builder,
  Utils as QbUtils,
  Config,
  ImmutableTree,
  BuilderProps,
  Fields,
  JsonTree,
} from '@react-awesome-query-builder/antd';

import { useState, useCallback, useEffect, useRef } from 'react';
import loadConfig from './config';
import './queryBuilder.css';
import './multiSelect.css';
import TypeAndQueryCard from './TypeAndQueryCard';
import SQLQueryBuilderCustomUtils from './custom_utils/SQLQueryBuilderCustomUtils';
import { useValidation } from './custom_utils/useValidation';
import { queryValidation } from './custom_utils/queryValidation';
import transformJsonDataQueryToGenerateStateOptimized from './custom_utils/transformJsonDataQueryToGenerateStateOptimized';
import convertToImmutableTreeComplete from './custom_utils/convertToImmutableTreeComplete';

interface DemoQueryBuilderState {
  tree: ImmutableTree;
  config: Config;
  skin: string;
  renderBocks: Record<string, boolean>;
  spelStr: string;
  spelErrors: Array<string>;
}

const WhenConditionCard = ({
  fields,
  onSave,
  value,
  index
}: {
  fields: Fields;
  onSave: Function;
  value: any;
  index: any,
}) => {
  const loadedConfig = loadConfig('antd', fields);
  const emptyInitValue: JsonTree = { id: QbUtils.uuid(), type: 'group' };
  const [thenObj, setThenObj] = useState<any>({
    query: value.thenQuery,
    type: value.thenType,
    value: value.thenValue,
    error: value.error
  });
  const [whenObj, setWhenObj] = useState<any>({
    query: value.when,
    value: value.whenValue,
    error: value.error
  });
  const initTree: ImmutableTree = convertToImmutableTreeComplete(
    transformJsonDataQueryToGenerateStateOptimized(
      value.when || emptyInitValue
    )
  );
  const [state, setState] = useState<DemoQueryBuilderState>({
    tree: initTree,
    config: loadedConfig,
    skin: 'antd',
    spelStr: '',
    spelErrors: [] as Array<string>,
    renderBocks: {
      validation: true,
      jsonlogic: true,
      elasticSearch: true,
      mongo: true,
      jsTree: true,
      spel: true,
      strings: true,
      sql: true,
    },
  });

  // Use refs to track previous values and avoid unnecessary updates
  const prevTreeRef = useRef(state.tree);
  const isInitialMount = useRef(true);
  const prevSavedDataRef = useRef<string>('');
  
  // Refs for DOM manipulation
  const isMountedRef = useRef(false);
  const observerRef = useRef<MutationObserver | null>(null);
  const intervalRef = useRef<NodeJS.Timeout | null>(null);

  const { renderValidationBlock } = useValidation(state, setState, 'QUERY');

  const renderBuilder = useCallback(
    (props: BuilderProps) => (
      <div className="query-builder-container">
        <div className="query-builder">
          <Builder {...props} />
        </div>
      </div>
    ),
    []
  );

  const onChange = useCallback(
    (immutableTree: ImmutableTree, config: Config) => {
      setState((prevState) => ({
        ...prevState,
        tree: immutableTree,
        config,
      }));

      setWhenObj({
        query: QbUtils.getTree(immutableTree),
        value: SQLQueryBuilderCustomUtils.formatSql(
          QbUtils.sqlFormat(immutableTree, loadedConfig)
        ),
      });
    },
    [loadedConfig]
  );

  useEffect(() => {
    const obj = {
      tree: QbUtils.loadTree(whenObj.query || emptyInitValue),
      config: loadedConfig,
    };
    const whenError = queryValidation(obj);

    const newData = {
      when: whenObj.query,
      whenSql: SQLQueryBuilderCustomUtils.formatSql(
        QbUtils.sqlFormat(state.tree, loadedConfig)
      ),
      whenValue: SQLQueryBuilderCustomUtils.formatSql(
        QbUtils.sqlFormat(state.tree, loadedConfig)
      ),
      whenError: whenError,
      whenJsonLogic: QbUtils.jsonLogicFormat(state.tree, loadedConfig),
      thenValue: thenObj.value,
      thenQuery: thenObj.query,
      thenSql: thenObj.sql,
      thenType: thenObj.type,
      thenJsonLogic: thenObj.jsonLogic,
      thenError: thenObj.error
    };

    // Create a stable string representation to compare
    const newDataStr = JSON.stringify(newData);

    // Only call onSave if data actually changed
    if (prevSavedDataRef.current !== newDataStr) {
      prevSavedDataRef.current = newDataStr;
      onSave(newData);
    }
  }, [whenObj, thenObj, state.tree, loadedConfig, onSave]);

  const onTypeSave = useCallback((data: any) => {
    setThenObj(data);
  }, []);

  // Initialize mount status
  useEffect(() => {
    isMountedRef.current = true;
    
    return () => {
      isMountedRef.current = false;
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      if (observerRef.current) {
        observerRef.current.disconnect();
      }
    };
  }, []);

  // Function to setup conjunctions
  const setupConjunctions = () => {
    if (!isMountedRef.current) return;

    const lastRendered = new WeakMap<Element, string>();

    /**
     * Returns the conjunction type (AND/OR) for a given .group element.
     * Falls back to parent if no buttons are found.
     */
    const getGroupConjunction = (group: Element): string => {
      const buttons = group.querySelectorAll(":scope > .group--header .group--conjunctions button");
      for (const btn of Array.from(buttons)) {
        if (
          btn.classList.contains("ant-btn-primary") ||
          btn.classList.contains("checked") ||
          btn.getAttribute("aria-pressed") === "true"
        ) {
          return btn.textContent?.trim().toUpperCase() || "AND";
        }
      }
      // If no conjunction found locally, inherit from parent
      const parentGroup = group.closest(".group")?.parentElement?.closest(".group");
      return parentGroup ? getGroupConjunction(parentGroup) : "AND";
    };

    /**
     * Change conjunction by clicking the appropriate button in the group header
     */
    const changeConjunction = (group: Element, newConjunction: string) => {
      const conjunctionButtons = group.querySelectorAll(":scope > .group--header .group--conjunctions button");

      // Filter to only AND/OR buttons (exclude NOT)
      const andOrButtons = Array.from(conjunctionButtons).filter(btn => {
        const text = btn.textContent?.trim().toUpperCase();
        return text === 'AND' || text === 'OR';
      });

      // Find and click the button for the selected conjunction
      for (const btn of andOrButtons) {
        const btnText = btn.textContent?.trim().toUpperCase();
        if (btnText === newConjunction) {
          (btn as HTMLButtonElement).click();
          break;
        }
      }
    };

    /**
     * Add conjunction dropdowns between all direct children of a group.
     */
    const updateGroupSeparators = (group: Element) => {
      const conjunction = getGroupConjunction(group);
      if (lastRendered.get(group) === conjunction) return;
      lastRendered.set(group, conjunction);

      const childContainers = group.querySelectorAll(":scope > .group--children > .group-or-rule-container");

      childContainers.forEach((container, idx) => {
        const existing = container.querySelector(".conjunction-separator-container");
        if (idx < childContainers.length - 1) {
          if (!existing) {
            // Create container
            const containerEl = document.createElement("div");
            containerEl.className = "conjunction-separator-container";

            // Create select element
            const selectEl = document.createElement("select");
            selectEl.className = "conjunction-dropdown";
            selectEl.innerHTML = `
               <option value="AND" ${conjunction === 'AND' ? 'selected' : ''}>AND</option>
               <option value="OR" ${conjunction === 'OR' ? 'selected' : ''}>OR</option>
             `;

            // Add event listeners for dynamic styles
            selectEl.addEventListener('mouseenter', () => {
              selectEl.style.transform = 'scale(1.02)';
            });
            selectEl.addEventListener('mouseleave', () => {
              selectEl.style.transform = 'scale(1)';
            });
            selectEl.addEventListener('focus', () => {
              selectEl.style.boxShadow = '0 0 0 2px rgba(24, 144, 255, 0.2)';
            });
            selectEl.addEventListener('blur', () => {
              selectEl.style.boxShadow = 'none';
            });

            // Add change handler
            selectEl.addEventListener('change', (e) => {
              e.stopPropagation();
              const newValue = (e.target as HTMLSelectElement).value;
              changeConjunction(group, newValue);
            });

            containerEl.appendChild(selectEl);
            container.appendChild(containerEl);
          } else {
            // Update existing dropdown value
            const selectEl = existing.querySelector('select');
            if (selectEl && selectEl.value !== conjunction) {
              selectEl.value = conjunction;
            }
          }
        } else if (existing) {
          existing.remove();
        }
      });
    };

    /**
     * Update all visible groups
     */
    const updateAllGroups = () => {
      const groups = document.querySelectorAll(".query-builder .group");
      groups.forEach((group) => {
        updateGroupSeparators(group);
      });
    };

    updateAllGroups();

    // Setup mutation observer for future changes
    const queryBuilder = document.querySelector(".query-builder-container");
    if (!queryBuilder) return;

    // Disconnect existing observer
    if (observerRef.current) {
      observerRef.current.disconnect();
    }

    const observer = new MutationObserver((mutations) => {
      const affectedGroups = new Set<Element>();
      for (const mutation of mutations) {
        if (mutation.target instanceof Element) {
          const group = mutation.target.closest(".group");
          if (group) affectedGroups.add(group);
        }
      }
      if (affectedGroups.size > 0) {
        requestAnimationFrame(() => {
          affectedGroups.forEach((group) => updateGroupSeparators(group));
        });
      }
    });

    observer.observe(queryBuilder, {
      childList: true,
      subtree: true,
      attributes: true,
      attributeFilter: ["class", "aria-pressed"],
    });

    observerRef.current = observer;
  };

  // Initialize conjunctions on mount and when tree changes
  useEffect(() => {
    // Clear any existing interval
    if (intervalRef.current) {
      clearInterval(intervalRef.current);
    }

    // Set up an interval to check for the Query Builder DOM
    intervalRef.current = setInterval(() => {
      const queryBuilder = document.querySelector(".query-builder .group");
      if (queryBuilder) {
        // DOM is ready, setup conjunctions
        setupConjunctions();
        clearInterval(intervalRef.current!);
        intervalRef.current = null;
      }
    }, 100);

    // Cleanup after 5 seconds if still not found
    const timeoutId = setTimeout(() => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
        intervalRef.current = null;
      }
    }, 5000);

    return () => {
      if (intervalRef.current) {
        clearInterval(intervalRef.current);
      }
      clearTimeout(timeoutId);
    };
  }, []); // Run on mount

  // Setup conjunctions when tree changes
  useEffect(() => {
    if (isInitialMount.current || prevTreeRef.current === state.tree) {
      if (isInitialMount.current) isInitialMount.current = false;
      prevTreeRef.current = state.tree;
      return;
    }
    prevTreeRef.current = state.tree;

    // Setup conjunctions when tree changes
    setupConjunctions();
  }, [state.tree]);

  return (
    <main className="when-main-container">
      <section className="when-container">
        <span className='mutiple-title'>{index === 0 ? "IF" : "ELSE IF"}</span>
        <section className='when-section-container'>

          <Query
            {...state.config}
            value={state.tree}
            onChange={onChange}
            renderBuilder={renderBuilder}
          />
        </section>
        {/* {renderValidationBlock()} */}
      </section>
      <section className="then-main-container">
        <TypeAndQueryCard
          onSave={onTypeSave}
          data={thenObj}
          fields={fields}
          title="THEN"
        />
      </section>
    </main>
  );
};

export default WhenConditionCard;